/**
 * ProgressBar Component
 *
 * Reusable progress indicator for translation jobs and batch operations.
 * Supports determinate and indeterminate modes with color variants.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

const { __ } = wp.i18n;

/**
 * ProgressBar class
 *
 * @class ProgressBar
 */
export default class ProgressBar {
    /**
     * Constructor
     *
     * @param {Object} options - Configuration options
     * @param {HTMLElement|string} options.container - Container element or selector to render into
     * @param {number} [options.value=0] - Current progress value (0-100)
     * @param {number} [options.max=100] - Maximum value
     * @param {string} [options.label=''] - Optional label text
     * @param {string} [options.status='pending'] - Status: pending|in-progress|completed|failed
     * @param {string} [options.variant='default'] - Color variant (default|success|warning|error)
     * @param {boolean} [options.animated=false] - Enable striped animation
     * @param {boolean} [options.indeterminate=false] - Enable indeterminate/loading state
     * @param {boolean} [options.showLabel=true] - Show label inside bar
     * @param {boolean} [options.showPercentage=true] - Show percentage text
     * @param {Function} [options.onComplete] - Callback when progress reaches 100%
     */
    constructor(options = {}) {
        // Handle container as selector or element
        this.container = typeof options.container === 'string'
            ? document.querySelector(options.container)
            : options.container;

        this.value = this._clampValue(options.value || 0, options.max || 100);
        this.max = options.max || 100;
        this.label = options.label || '';
        this.status = this._validateStatus(options.status || 'pending');
        this.variant = this._validateVariant(options.variant);
        this.animated = options.animated || false;
        this.indeterminate = options.indeterminate || false;
        this.showLabel = options.showLabel !== false;
        this.showPercentage = options.showPercentage !== false;
        this.onComplete = options.onComplete || null;

        // Element references
        this.element = null;
        this.barElement = null;
        this.labelElement = null;
        this.percentageElement = null;

        // State tracking
        this.isComplete = false;
        this.animationFrame = null;

        // Validate container
        if (!this.container || !(this.container instanceof HTMLElement)) {
            throw new Error('ProgressBar requires a valid container element');
        }

        this._init();
    }

    /**
     * Initialize the progress bar
     *
     * @private
     */
    _init() {
        this._render();
        this._updateProgress();
    }

    /**
     * Render the progress bar HTML
     *
     * @private
     */
    _render() {
        // Create wrapper element
        this.element = document.createElement('div');
        this.element.className = `mpz-progress-bar mpz-progress-bar--${this.variant}`;

        // Add status-based variant if no explicit variant set
        if (!this.variant || this.variant === 'default') {
            const statusVariants = {
                'pending': 'default',
                'in-progress': 'default',
                'completed': 'success',
                'failed': 'error'
            };
            const variantFromStatus = statusVariants[this.status];
            if (variantFromStatus && variantFromStatus !== 'default') {
                this.element.classList.remove('mpz-progress-bar--default');
                this.element.classList.add(`mpz-progress-bar--${variantFromStatus}`);
            }
        }

        if (this.indeterminate) {
            this.element.classList.add('mpz-progress-bar--indeterminate');
        }

        if (this.animated && this.status === 'in-progress') {
            this.element.classList.add('mpz-progress-bar--animated');
        }

        // Create label if provided
        if (this.label) {
            this.labelElement = document.createElement('div');
            this.labelElement.className = 'mpz-progress-bar__label';
            this.labelElement.textContent = this.label;
            this.element.appendChild(this.labelElement);
        }

        // Create progress track
        const trackElement = document.createElement('div');
        trackElement.className = 'mpz-progress-bar__track';
        trackElement.setAttribute('role', 'progressbar');
        trackElement.setAttribute('aria-valuemin', '0');
        trackElement.setAttribute('aria-valuemax', this.max.toString());
        trackElement.setAttribute('aria-valuenow', this.value.toString());

        if (this.label) {
            trackElement.setAttribute('aria-label', this.label);
        }

        if (this.indeterminate) {
            trackElement.setAttribute('aria-valuenow', '0');
            trackElement.setAttribute('aria-valuetext', __('Loading', 'multilingual-press-zone'));
        } else {
            const percentage = this._getPercentage();
            trackElement.setAttribute('aria-valuetext', `${percentage}%`);
        }

        // Create progress bar
        this.barElement = document.createElement('div');
        this.barElement.className = 'mpz-progress-bar__bar';
        trackElement.appendChild(this.barElement);

        // Create percentage text
        if (this.showPercentage && !this.indeterminate) {
            this.percentageElement = document.createElement('div');
            this.percentageElement.className = 'mpz-progress-bar__percentage';
            this.percentageElement.textContent = `${this._getPercentage()}%`;
            trackElement.appendChild(this.percentageElement);
        }

        this.element.appendChild(trackElement);

        // Append to container
        this.container.appendChild(this.element);
    }

    /**
     * Update progress bar display
     *
     * @private
     */
    _updateProgress() {
        if (!this.barElement) return;

        const percentage = this._getPercentage();

        // Cancel any pending animation
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }

        // Use requestAnimationFrame for smooth updates
        this.animationFrame = requestAnimationFrame(() => {
            // Update bar width
            this.barElement.style.width = `${percentage}%`;

            // Update ARIA attributes
            const trackElement = this.barElement.parentElement;
            if (trackElement) {
                trackElement.setAttribute('aria-valuenow', this.value.toString());
                trackElement.setAttribute('aria-valuetext', `${percentage}%`);
            }

            // Update percentage text
            if (this.percentageElement) {
                this.percentageElement.textContent = `${percentage}%`;
            }

            // Check if complete
            if (percentage >= 100 && !this.isComplete) {
                this.isComplete = true;
                this.element.classList.add('mpz-progress-bar--complete');

                // Trigger completion callback
                if (typeof this.onComplete === 'function') {
                    this.onComplete();
                }
            }
        });
    }

    /**
     * Calculate percentage
     *
     * @private
     * @return {number} Percentage value (0-100)
     */
    _getPercentage() {
        if (this.max === 0) return 0;
        return Math.round((this.value / this.max) * 100);
    }

    /**
     * Clamp value between 0 and max
     *
     * @private
     * @param {number} value - Value to clamp
     * @param {number} max - Maximum value
     * @return {number} Clamped value
     */
    _clampValue(value, max) {
        return Math.max(0, Math.min(value, max));
    }

    /**
     * Validate and normalize variant
     *
     * @private
     * @param {string} [variant='default'] - Variant name
     * @return {string} Valid variant name
     */
    _validateVariant(variant = 'default') {
        const validVariants = ['default', 'success', 'warning', 'error'];
        return validVariants.includes(variant) ? variant : 'default';
    }

    /**
     * Validate and normalize status
     *
     * @private
     * @param {string} [status='pending'] - Status name
     * @return {string} Valid status name
     */
    _validateStatus(status = 'pending') {
        const validStatuses = ['pending', 'in-progress', 'completed', 'failed'];
        return validStatuses.includes(status) ? status : 'pending';
    }

    /**
     * Set progress value
     *
     * @param {number} value - New value (0-max)
     * @param {number} [max] - Optional new max value
     */
    setValue(value, max) {
        if (typeof max === 'number' && max > 0) {
            this.max = max;
        }

        this.value = this._clampValue(value, this.max);
        this._updateProgress();
    }

    /**
     * Increment progress value
     *
     * @param {number} [amount=1] - Amount to increment by
     */
    increment(amount = 1) {
        this.setValue(this.value + amount);
    }

    /**
     * Set progress label
     *
     * @param {string} label - New label text
     */
    setLabel(label) {
        this.label = label;

        if (!this.labelElement && label) {
            // Create label if it doesn't exist
            this.labelElement = document.createElement('div');
            this.labelElement.className = 'mpz-progress-bar__label';
            this.element.insertBefore(this.labelElement, this.element.firstChild);
        }

        if (this.labelElement) {
            this.labelElement.textContent = label;

            // Update ARIA label
            const trackElement = this.element.querySelector('.mpz-progress-bar__track');
            if (trackElement) {
                trackElement.setAttribute('aria-label', label);
            }
        }
    }

    /**
     * Set variant
     *
     * @param {string} variant - New variant (default|success|warning|error)
     */
    setVariant(variant) {
        // Remove old variant class
        this.element.classList.remove(`mpz-progress-bar--${this.variant}`);

        // Set and add new variant
        this.variant = this._validateVariant(variant);
        this.element.classList.add(`mpz-progress-bar--${this.variant}`);
    }

    /**
     * Set status
     *
     * @param {string} status - New status (pending|in-progress|completed|failed)
     */
    setStatus(status) {
        const oldStatus = this.status;
        this.status = this._validateStatus(status);

        // Update variant based on status
        const statusVariants = {
            'pending': 'default',
            'in-progress': 'default',
            'completed': 'success',
            'failed': 'error'
        };

        // Remove old variant
        const oldVariant = statusVariants[oldStatus];
        if (oldVariant) {
            this.element.classList.remove(`mpz-progress-bar--${oldVariant}`);
        }

        // Add new variant
        const newVariant = statusVariants[this.status];
        if (newVariant) {
            this.element.classList.add(`mpz-progress-bar--${newVariant}`);
        }

        // Handle animated state for in-progress
        if (this.status === 'in-progress' && this.animated) {
            this.element.classList.add('mpz-progress-bar--animated');
        } else {
            this.element.classList.remove('mpz-progress-bar--animated');
        }

        // Auto-complete if status is completed
        if (this.status === 'completed') {
            this.complete();
        }
    }

    /**
     * Toggle indeterminate mode
     *
     * @param {boolean} indeterminate - Whether to enable indeterminate mode
     */
    setIndeterminate(indeterminate) {
        this.indeterminate = !!indeterminate;

        if (this.indeterminate) {
            this.element.classList.add('mpz-progress-bar--indeterminate');

            // Update ARIA
            const trackElement = this.element.querySelector('.mpz-progress-bar__track');
            if (trackElement) {
                trackElement.setAttribute('aria-valuenow', '0');
                trackElement.setAttribute('aria-valuetext', __('Loading', 'multilingual-press-zone'));
            }

            // Hide percentage
            if (this.percentageElement) {
                this.percentageElement.style.display = 'none';
            }
        } else {
            this.element.classList.remove('mpz-progress-bar--indeterminate');

            // Show percentage
            if (this.percentageElement) {
                this.percentageElement.style.display = '';
            }

            this._updateProgress();
        }
    }

    /**
     * Reset progress to zero
     */
    reset() {
        this.value = 0;
        this.isComplete = false;
        this.element.classList.remove('mpz-progress-bar--complete');
        this._updateProgress();
    }

    /**
     * Complete the progress (set to 100%)
     */
    complete() {
        this.setValue(this.max);
    }

    /**
     * Destroy the progress bar
     */
    destroy() {
        // Cancel any pending animations
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }

        // Remove from DOM
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }

        // Clear references
        this.element = null;
        this.barElement = null;
        this.labelElement = null;
        this.percentageElement = null;
        this.container = null;
        this.onComplete = null;
    }

    /**
     * Get current progress percentage
     *
     * @return {number} Current percentage (0-100)
     */
    getPercentage() {
        return this._getPercentage();
    }

    /**
     * Check if progress is complete
     *
     * @return {boolean} True if at 100%
     */
    isCompleted() {
        return this.isComplete;
    }
}
